import React, { useState, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";

export const CurrencyInput = ({
  value,
  onChange,
  placeholder = "0.00",
  prefix = "$",
  className = "",
  id = "currency-input",
  max = Number.MAX_SAFE_INTEGER,
  min = 0,
  disabled = false,
  allowNegative = false,
  decimals = 2,
}: {
  value: string;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  prefix?: string;
  className?: string;
  id?: string;
  max?: number;
  min?: number;
  disabled?: boolean;
  allowNegative?: boolean;
  decimals?: number;
}) => {
  const formatValue = (val: string) => {
    if (!val && val !== "0") {
      return "";
    }

    let number = val;

    // Convert to string and remove non-numeric characters except decimal point and minus
    number = val.toString().replace(/[^\d.-]/g, "");

    // Handle negative numbers if allowed
    const isNegative = allowNegative && number.startsWith("-");
    if (isNegative) {
      number = number.substring(1); // Remove the first minus sign
    }

    // Remove any additional minus signs
    number = number.replace(/-/g, "");

    // Split into integer and decimal parts
    let [integer, decimal] = number.split(".");

    // Add thousand separators to integer part
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Format decimal part
    if (decimal !== undefined) {
      decimal = decimal.slice(0, decimals);
    }

    // Combine parts
    let formatted = isNegative ? "-" : "";
    formatted += integer;
    if (decimal !== undefined) {
      formatted += "." + decimal;
    }

    return formatted;
  };

  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Remove formatting for processing
  const unformatValue = (val: string) => {
    return val.toString().replace(/[^\d.-]/g, "");
  };

  useEffect(() => {
    // Set cursor position after render
    if (inputRef.current) {
      inputRef.current.setSelectionRange(cursor, cursor);
    }
  }, [cursor, value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value.replace(prefix, "").trim();
    const cursorPosition = input.selectionStart;

    // Count non-numeric characters before cursor (for position adjustment)
    const valueBeforeCursor = rawValue.substring(0, cursorPosition ?? 0);
    const oldCommaCount = (valueBeforeCursor.match(/,/g) || []).length;

    // Process raw value
    const unformatted = unformatValue(rawValue);

    // Check if value is within min/max constraints
    const numValue = parseFloat(unformatted);
    if (!isNaN(numValue)) {
      const effectiveMin = allowNegative ? -max : min;
      if (numValue > max || numValue < effectiveMin) {
        return;
      }
    }

    // Format the value
    const formatted = formatValue(unformatted);

    // Calculate new cursor position
    const newValueBeforeCursor = formatted.substring(0, cursorPosition ?? 0);
    const newCommaCount = (newValueBeforeCursor.match(/,/g) || []).length;
    const cursorAdjustment = newCommaCount - oldCommaCount;
    const newCursorPosition = cursorPosition
      ? cursorPosition + cursorAdjustment
      : 0;

    setCursor(newCursorPosition);

    // Call the onChange handler with the numeric value or null if empty
    if (onChange) {
      if (!unformatted) {
        onChange(null);
        return;
      }
      // Allow intermediate decimal states (e.g. "." or "12.") while typing
      const isIntermediateDecimal =
        unformatted === "." || unformatted.endsWith(".");
      const isIntermediateNegative =
        allowNegative && (unformatted === "-" || unformatted === "-.");
      if (isIntermediateNegative) {
        onChange(unformatted);
        return;
      }
      if (isIntermediateDecimal) {
        onChange(unformatted);
        return;
      }
      const numericValue = parseFloat(unformatted);
      onChange(isNaN(numericValue) ? null : unformatted);
    }
  };

  return (
    <div className="currency-input-container">
      <div className="currency-input-wrapper relative">
        {prefix && (
          <span className="currency-prefix absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {prefix}
          </span>
        )}
        <Input
          type="text"
          value={formatValue(value)}
          ref={inputRef}
          className={cn("pl-5", className)}
          id={id}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
